<template>
  <div class="ai-chat-root">
    <div
      class="ai-float-btn"
      :style="{ left: posX + 'px', top: posY + 'px' }"
      @mousedown="startDrag"
      @touchstart="startDrag"
      title="灵机"
    >
      <div class="float-btn-inner">
        <svg viewBox="0 0 40 40" width="48" height="48">
          <defs>
            <linearGradient id="floatGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#2BBD6E" />
              <stop offset="100%" style="stop-color:#0D966A" />
            </linearGradient>
          </defs>
          <circle cx="20" cy="20" r="20" fill="url(#floatGrad)" />
          <text x="20" y="22" text-anchor="middle" dominant-baseline="middle" fill="white" font-size="15" font-weight="bold">灵机</text>
        </svg>
        <div class="breath-ring"></div>
      </div>
    </div>
    <ai-chat-dialog ref="aiDialog" :show="showDialog" :button-x="posX" :button-y="posY" @close="showDialog = false"></ai-chat-dialog>
  </div>
</template>

<script>
import AiChatDialog from './AiChatDialog.vue'
import { EventBus } from '../../js/eventbus.js'
export default {
  name: 'AiChat',
  components: { AiChatDialog },
  data() {
    return {
      showDialog: false,
      dragging: false,
      hasDragged: false,
      posX: 0,
      posY: 0,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0
    }
  },
  mounted() {
    // 初始位置：右下角，距离右侧40px，底部80px
    this.$nextTick(() => {
      const btnWidth = 84 // 按钮宽度
      const btnHeight = 84 // 按钮高度
      this.posX = window.innerWidth - 40 - btnWidth
      this.posY = window.innerHeight - 80 - btnHeight
    })
    // 监听窗口大小变化，调整位置防止超出视口
    window.addEventListener('resize', this.handleResize)
    // 监听AI输入文本事件
    EventBus.$on('ai-input-text', (payload) => {
      // 无论弹框是否已打开，都确保对话框可见
      this.showDialog = true
      // 确保对话框已渲染后设置输入文本
      this.$nextTick(() => {
        if (this.$refs.aiDialog) {
          // 标记为联动触发，跳过初始加载
          this.$refs.aiDialog.skipInitialLoad = true
          // 不主动创建session，交给AiChatPanel在发送时延迟创建
          EventBus.$emit('set-ai-input-text', payload.text)
        }
      })
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.removeDragListeners()
    // 移除事件监听器
    EventBus.$off('ai-input-text')
  },
  methods: {
    startDrag(event) {
      // 阻止事件冒泡，避免触发父元素事件
      event.stopPropagation()
      this.dragging = true
      this.hasDragged = false
      // 获取初始指针位置
      const isTouch = event.type.startsWith('touch')
      const clientX = isTouch ? event.touches[0].clientX : event.clientX
      const clientY = isTouch ? event.touches[0].clientY : event.clientY
      this.startX = clientX
      this.startY = clientY
      this.offsetX = clientX - this.posX
      this.offsetY = clientY - this.posY
      // 添加全局事件监听
      this.addDragListeners()
    },
    onDrag(event) {
      if (!this.dragging) return
      // 阻止默认行为，防止滚动
      event.preventDefault()
      const isTouch = event.type.startsWith('touch')
      const clientX = isTouch ? event.touches[0].clientX : event.clientX
      const clientY = isTouch ? event.touches[0].clientY : event.clientY
      // 计算移动距离，如果超过阈值则标记为拖拽
      const deltaX = clientX - this.startX
      const deltaY = clientY - this.startY
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
      // 增加阈值，减少误判，避免轻微移动就被判定为拖拽
      if (distance > 15) {
        this.hasDragged = true
      }
      // 计算新位置
      let newX = clientX - this.offsetX
      let newY = clientY - this.offsetY
      // 边界限制，防止拖出视口
      const btnWidth = 84
      const btnHeight = 84
      const maxX = window.innerWidth - btnWidth
      const maxY = window.innerHeight - btnHeight
      newX = Math.max(0, Math.min(newX, maxX))
      newY = Math.max(0, Math.min(newY, maxY))
      this.posX = newX
      this.posY = newY
    },
    stopDrag() {
      // 防重复：触屏设备上 mouseup 与 touchend 可能同时触发，仅在拖拽活跃期间处理一次
      if (!this.dragging) return
      this.dragging = false
      this.removeDragListeners()
      // 未发生拖拽则视为点击，打开弹框
      if (!this.hasDragged) {
        this.showDialog = true
      }
      this.hasDragged = false
    },
    addDragListeners() {
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.stopDrag)
      document.addEventListener('touchmove', this.onDrag, { passive: false })
      document.addEventListener('touchend', this.stopDrag)
    },
    removeDragListeners() {
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDrag)
      document.removeEventListener('touchmove', this.onDrag)
      document.removeEventListener('touchend', this.stopDrag)
    },
    handleResize() {
      // 窗口大小变化时，确保按钮仍在视口内
      const btnWidth = 84
      const btnHeight = 84
      const maxX = window.innerWidth - btnWidth
      const maxY = window.innerHeight - btnHeight
      this.posX = Math.max(0, Math.min(this.posX, maxX))
      this.posY = Math.max(0, Math.min(this.posY, maxY))
    }
  }
}
</script>

<style scoped>
.ai-chat-root {
  position: fixed;
  z-index: 9999;
}
.ai-float-btn {
  position: fixed;
  /* 位置由JS动态控制 */
  cursor: move;
  z-index: 10000;
  user-select: none; /* 防止拖拽时选中文本 */
  touch-action: none; /* 防止触摸默认行为 */
}
.ai-float-btn:active {
  cursor: grabbing;
}
.float-btn-inner {
  position: relative;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 6px 24px rgba(43, 189, 110, 0.35);
}
.float-btn-inner:hover {
  transform: scale(1.1);
  box-shadow: 0 9px 36px rgba(43, 189, 110, 0.45);
}
.breath-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: linear-gradient(135deg, #2BBD6E, #0D966A);
  opacity: 0;
  animation: breathe 3s ease-in-out infinite;
  pointer-events: none;
}
@keyframes breathe {
  0% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.5); opacity: 0; }
  100% { transform: scale(1); opacity: 0; }
}
</style>
